import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { ApiFailure, api } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy } from '../copy/en-CA.ts';
import { describePasswordFailure } from '../describe.ts';
import { useSession } from '../session.tsx';

const c = copy.changePassword;

/**
 * Choosing a password. Two ways in: forced, and the only thing shown, while
 * a temporary password is still in force ("temporary"); or by choice, from
 * "Change password" in the header, inside the app ("own").
 *
 * The forced page does not ask for the temporary password: the person typed
 * it to sign in a moment ago, and the server accepts the change without it
 * for 15 minutes after that sign-in. After that the server signs them out
 * ("signInAgain"), and this page says why and sends them back to sign in.
 */
export function ChangePasswordPage({ mode = 'temporary' }: { mode?: 'temporary' | 'own' }) {
  const { refresh, setUser } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [working, setWorking] = useState(false);
  /** The server ended the session: the only way on is signing in again. */
  const [signedOut, setSignedOut] = useState(false);
  const signInAgainButton = useRef<HTMLButtonElement>(null);
  const own = mode === 'own';

  // The form the person was using has gone; put keyboard focus on the way on.
  useEffect(() => {
    if (signedOut) signInAgainButton.current?.focus();
  }, [signedOut]);

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next !== confirm) {
      setError(c.mismatch);
      return;
    }
    setWorking(true);
    try {
      await api.changePassword(own ? current : null, next);
      if (own) {
        setCurrent('');
        setNext('');
        setConfirm('');
        setDone(true);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(describePasswordFailure(err, mode));
      if (err instanceof ApiFailure && err.code === 'signInAgain') setSignedOut(true);
    } finally {
      setWorking(false);
    }
  };

  const signOut = async () => {
    await api.signOut();
    setUser(null);
  };

  // The server ended the session when it answered "signInAgain"; signing out
  // here as well makes sure of it, and the sign-in screen shows even if that fails.
  const backToSignIn = async () => {
    try {
      await api.signOut();
    } finally {
      setUser(null);
    }
  };

  if (signedOut) {
    return (
      <div className="signin-page">
        <div className="wordmark">
          <span className="wordmark-name">{copy.appName}</span>
          <span className="wordmark-sub">{copy.appSub}</span>
        </div>
        <div className="card signin-card">
          <div className="page-title">
            <h1>{c.title}</h1>
          </div>
          {error ? <Notice kind="error">{error}</Notice> : null}
          <div className="row">
            <button
              ref={signInAgainButton}
              type="button"
              className="btn primary"
              onClick={() => {
                void backToSignIn();
              }}
            >
              {c.signInAgainButton}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const form = (
    <form
      className="card signin-card"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <div className="page-title">
        <h1>{own ? c.own.title : c.title}</h1>
        <p>{own ? c.own.intro : c.intro}</p>
      </div>
      {own ? (
        <div className="field">
          <label htmlFor="current">{c.own.current}</label>
          <input id="current" className="input" type="password" autoComplete="current-password" required value={current} onChange={(e) => { setCurrent(e.target.value); }} />
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="next">{c.next}</label>
        <input id="next" className="input" type="password" autoComplete="new-password" required minLength={10} value={next} onChange={(e) => { setNext(e.target.value); }} />
      </div>
      <div className="field">
        <label htmlFor="confirm">{c.confirm}</label>
        <input id="confirm" className="input" type="password" autoComplete="new-password" required minLength={10} value={confirm} onChange={(e) => { setConfirm(e.target.value); }} />
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {done ? <Notice kind="success">{c.own.done}</Notice> : null}
      <div className="row">
        <button type="submit" className="btn primary" disabled={working}>
          {working ? c.working : c.submit}
        </button>
        {own ? (
          <Link className="btn" to="/">
            {c.own.back}
          </Link>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => {
              void signOut();
            }}
          >
            {copy.signOut}
          </button>
        )}
      </div>
    </form>
  );

  if (own) return form;
  return (
    <div className="signin-page">
      <div className="wordmark">
        <span className="wordmark-name">{copy.appName}</span>
        <span className="wordmark-sub">{copy.appSub}</span>
      </div>
      {form}
    </div>
  );
}
